import { proxy, snapshot, subscribe, useSnapshot } from "valtio";
import { EnumLeverageProductsIndication } from "src/api/client/extended-search.api.types";
import { IKeyFigureValue, ISelectedRanking, ITagValue } from "src/components/pages/search/components/types";
import { ISortDirectionType } from "src/types/common";

export const ViewMode = {
    Grid: "grid",
    Table: "table",
} as const;

export type TViewMode = (typeof ViewMode)[keyof typeof ViewMode];

export interface ISearchStore {
    query: string;
    keyFigures: ReadonlyArray<IKeyFigureValue>;
    tags: ReadonlyArray<ITagValue>;
    selectedRanking: ISelectedRanking | null;
    sortDirection: ISortDirectionType;
    brokersIds: ReadonlyArray<string>;
    isInvestable: boolean;
    isAtInvestable: boolean;
    isChInvestable: boolean;
    leverageProductsIndication: EnumLeverageProductsIndication;
    view: TViewMode;
    selectedFigures: ReadonlyArray<string>;
    hasHydratedFromUrl: boolean;
}

export const getInitialSearchState = (): ISearchStore => ({
    query: "",
    keyFigures: [],
    tags: [],
    selectedRanking: null,
    sortDirection: null,
    brokersIds: [],
    isInvestable: true,
    isAtInvestable: true,
    isChInvestable: true,
    leverageProductsIndication: EnumLeverageProductsIndication.AllWikifolios,
    view: ViewMode.Grid,
    selectedFigures: [],
    hasHydratedFromUrl: false,
});

const searchStore = proxy<ISearchStore>(getInitialSearchState());

export interface ICommitSearchStateOptions {
    trackVisit?: boolean;
}

const userCommitListeners = new Set<(next: ISearchStore, options?: ICommitSearchStateOptions) => void>();

export const registerOnUserCommit = (handler: (next: ISearchStore, options?: ICommitSearchStateOptions) => void): (() => void) => {
    userCommitListeners.add(handler);
    return () => {
        userCommitListeners.delete(handler);
    };
};

export const subscribeToSearchStore = (listener: () => void): (() => void) => subscribe(searchStore, listener);

export const getSearchState = (): ISearchStore => snapshot(searchStore) as ISearchStore;

const applyState = (updater: (current: ISearchStore) => ISearchStore): ISearchStore => {
    const next = updater({ ...searchStore });
    Object.assign(searchStore, next);
    return next;
};

export const commitSearchState = (updater: (current: ISearchStore) => ISearchStore, options?: ICommitSearchStateOptions) => {
    const next = applyState(updater);
    userCommitListeners.forEach(listener => listener(next, options));
};

export const resetSearchStore = () => {
    Object.assign(searchStore, getInitialSearchState());
};

export const setSearchStateSilently = (updater: (current: ISearchStore) => ISearchStore) => {
    applyState(updater);
};

export function useSearchStore(): ISearchStore {
    return useSnapshot(searchStore) as ISearchStore;
}
